import { DateTime } from "luxon";

import { DocumentMapping, DynamicDocumentInstructions } from "@/configuration/document-mappings/types";
import autonomyProvidersData from "@/configuration/groups/autonomy/rules/live/providers.json";
import { type AutoIntakeConfig } from "@/src/lib/auto-intake/auto-intake-config";
import { AutoIntakeBlockCheck } from "@/src/lib/auto-intake/auto-intake-eligibility-requirements";
import { getReceivingProviderNpi, getReviewedFieldsForKey } from "@/src/lib/blocks/utils";
import { DocRoutingStrategy, ReferralRoutingStrategy } from "@/src/lib/constants";
import { isHmoPlan } from "@/src/lib/coverage-utils";
import { DEFAULT_ENCOUNTER_FILTER_OPTIONS, EncounterStatus } from "@/src/lib/ehr/customer/encounter-filters";
import type { FilterEncounterOptions } from "@/src/lib/ehr/customer/encounter-filters";
import type { EcwEncounterExclusions } from "@/src/lib/ehr/ecw/ecw-webecw-helpers";
import { EcwEncounter, EcwEncounterType } from "@/src/lib/ehr/ecw/webecw/endpoints/encounters";
import type { ProviderInfo } from "@/src/lib/types/provider.types";
import { calculateAge, formatPhoneNumber } from "@/src/lib/utils/date-and-phone-utils";
import {
  extractPractitionerNPIs,
  extractProviderICD10Map,
  getProvidersForDiagnosis,
  type ProviderICD10Rules,
  type ProvidersConfig,
} from "@/src/lib/utils/diagnosis-based-provider-matcher";
import { diagnosisCodeMatches } from "@/src/lib/utils/diagnosis-code-matcher";
import { AssigneeUnresolvedReason } from "@/src/lib/workflows/steps/assignee-resolution";
import { type StepOutputs } from "@/src/lib/workflows/steps/step-outputs";
import { BlockKey } from "@/src/models/blocks/block-types";
import {
  Classification,
  ClassificationSection,
  PROGRESS_NOTE_CLASSIFICATIONS,
} from "@/src/models/constants/classifications";
import { Workflow, WorkflowType } from "@/src/models/constants/group";
import { Diagnosis, isStatOrUrgentPriority, Referral } from "@/src/models/documents/referral";
import { Group } from "@/src/models/group";
import { formatICD10CodeForDisplay } from "@/src/models/icd10";
import { getPatientPhoneNumber, isPatientNotInEhr, Patient } from "@/src/models/patient";
import { Practitioner } from "@/src/models/practitioner";
import { Section } from "@/src/models/section";
import { Task } from "@/src/models/task";

const autonomyProviders = autonomyProvidersData as ProvidersConfig;

export const AUTONOMY_PRACTITIONER_NPIS = extractPractitionerNPIs(autonomyProviders);
const AUTONOMY_ICD10_MAP: Record<string, ProviderICD10Rules> = extractProviderICD10Map(autonomyProviders);

export function getAutonomyProvidersForDiagnosis(diagnosisList: Diagnosis[]): ProviderInfo[] {
  return getProvidersForDiagnosis(diagnosisList, AUTONOMY_ICD10_MAP, AUTONOMY_PRACTITIONER_NPIS);
}

export function hasAutonomyDiagnosisRules(npi: string | null): npi is string {
  return !!npi && npi in autonomyProviders.providers;
}

export function getAutonomyDocumentInstructions(
  baseMapping: DocumentMapping,
  stepOutputs?: StepOutputs
): DynamicDocumentInstructions {
  const resolution = stepOutputs?.resolve_assignee;

  if (!resolution) {
    return baseMapping;
  }

  const rule = baseMapping.assignedTo;

  if (resolution.resolved) {
    return {
      ...baseMapping,
      assignedTo: resolution.name,
      noteLabel: "Assignment",
      note: `Rule: ${rule}\n→ ${resolution.name} (matched in eCW staff)`,
    };
  }

  if (resolution.reason === AssigneeUnresolvedReason.ROSTER_GAP) {
    return {
      ...baseMapping,
      assignedTo: resolution.intendedName,
      noteLabel: "Assignment",
      note: `Rule: ${rule}\n→ ${resolution.intendedName} — not in eCW staff roster; routed to review`,
    };
  }

  return {
    ...baseMapping,
    noteLabel: "Assignment",
    note: `Rule: ${rule}\n→ no provider assignment; routed to review`,
  };
}

export function getAutonomyMissingReferralItems(task: Task): string[] {
  const missing: string[] = [];

  const sections = task.files.flatMap((f) => f.sections ?? []);
  const hasProgressNote = sections.some(
    (s) => s.classification && PROGRESS_NOTE_CLASSIFICATIONS.includes(s.classification)
  );

  if (!hasProgressNote) {
    missing.push("Progress Note");
  }

  if (!task.referral?.primaryCoverage) {
    missing.push("Insurance Information");
  }

  if (!task.patient?.homeAddress) {
    missing.push("Patient Address");
  }

  if (task.patient && !getPatientPhoneNumber(task.patient)) {
    missing.push("Patient Phone Number");
  }

  return missing;
}

const AUTONOMY_SUPPLEMENTARY_CLASSIFICATIONS = new Set<Classification>([
  Classification.ADMIN,
  Classification.BLANK,
  Classification.CONSENT,
  Classification.COVER_PAGE,
  Classification.FINANCIAL,
  Classification.IDENTIFICATION_CARD,
  Classification.INSURANCE_CARD,
  Classification.INTAKE,
  Classification.JUNK,
  Classification.LEGAL,
  Classification.MEDICAL_RECORD_REQUEST,
  Classification.OTHER,
  Classification.UNKNOWN,
]);

export function getAutonomyGroupingClassification(classifications: Classification[]): Classification | null {
  if (classifications.length <= 1) {
    return null;
  }

  const hasMedicalRecordRequest = classifications.includes(Classification.MEDICAL_RECORD_REQUEST);
  const substantive = classifications.filter((c) => !AUTONOMY_SUPPLEMENTARY_CLASSIFICATIONS.has(c));

  if (new Set(substantive).size !== substantive.length) {
    return null;
  }

  if (substantive.length === 0) {
    return hasMedicalRecordRequest ? Classification.MEDICAL_RECORD_REQUEST : null;
  }

  if (substantive.length === 1) {
    return substantive[0];
  }

  // Referral overrides everything
  if (substantive.includes(Classification.REFERRAL)) {
    return Classification.REFERRAL;
  }

  // Disability Forms (absorbs consent and medical record request, already filtered as supplementary)
  if (substantive.includes(Classification.ADMIN_DISABILITY_FORMS)) {
    return Classification.ADMIN_DISABILITY_FORMS;
  }

  // Hierarchy — first match wins
  const hierarchyMatch = [
    Classification.LAB_RESULTS_ORDER_FORM_NEEDS_SIGNATURE,
    Classification.DME_ORDER,
    Classification.CLEARANCE_REQUEST,
  ].find((c) => substantive.includes(c));

  if (hierarchyMatch) {
    return hierarchyMatch;
  }

  // Mixed substantive types (labs + progress note + imaging, etc.) → Medical History
  return Classification.MEDICAL_HISTORY;
}

export const autonomyClassificationsToExtract = [
  Classification.ADMIN_DISABILITY_FORMS,
  Classification.CARE_PLAN,
  Classification.CLEARANCE_REQUEST,
  Classification.CONSENT,
  Classification.DME_ORDER,
  Classification.IDENTIFICATION_CARD,
  Classification.IMAGING_RESULTS_EEG,
  Classification.IMAGING_RESULTS_EMG,
  Classification.IMAGING_RESULTS_MRI,
  Classification.IMAGING_RESULTS_OTHER,
  Classification.INSURANCE_CARD,
  Classification.INTAKE,
  Classification.LAB_RESULTS,
  Classification.MEDICAL_HISTORY,
  Classification.MEDICAL_RECORD_REQUEST,
  // Classification.PATIENT_FORMS,
  Classification.PHARMACY_RELATED,
  Classification.PRIOR_AUTHORIZATION,
  Classification.PROGRESS_NOTE,
  Classification.REFERRAL,
  Classification.REFERRAL_STATUS_REQUEST,
  Classification.REFERRAL_RESPONSE,
  Classification.LAB_RESULTS_ORDER_FORM_NEEDS_SIGNATURE,
] as Classification[];

export const autonomyClassificationsForDisplay = [
  ...autonomyClassificationsToExtract,
  Classification.FINANCIAL,
  Classification.LEGAL,
  Classification.OTHER,
  Classification.JUNK,
] as Classification[];

/**
 * Classifications to display when viewing a referral task.
 * When a referral section is present, only show these classifications.
 */
export const AUTONOMY_REFERRAL_RELATED_CLASSIFICATIONS = new Set([
  Classification.REFERRAL,
  Classification.INTAKE,
  Classification.IDENTIFICATION_CARD,
  Classification.INSURANCE_CARD,
]);

/**
 * Filters sections to show only referral-related classifications.
 * Used when a referral section is present to reduce clutter.
 */
export function filterAutonomyReferralSections(sections: Section[]): Section[] {
  return sections.filter((section) => AUTONOMY_REFERRAL_RELATED_CLASSIFICATIONS.has(section.classification));
}

/**
 * Returns the classifications to display for selection for the Autonomy group
 *
 * @returns The classifications to display for selection for the Autonomy group
 */
export function getAutonomyClassificationsToDisplayForSelection(): ClassificationSection[] {
  return [
    {
      label: "Classifications",
      options: autonomyClassificationsForDisplay,
    },
  ];
}

/**
 * Returns the workflows for the Autonomy group
 *
 * @param group - The group to get the workflows for
 * @returns The workflows for the Autonomy group
 */
export async function getAutonomyWorkflows(group: Group): Promise<Partial<Record<WorkflowType, Classification[]>>> {
  return {
    [Workflow.REFERRAL]: [Classification.REFERRAL],
    [Workflow.DOC_MANAGEMENT]: autonomyClassificationsToExtract.filter((c) => c !== Classification.REFERRAL),
  };
}

const PLANS_REQUIRING_REFERRAL = [
  "tricare prime",
  "triwest",
  "wellmed",
  "health select",
  "navigate",
  "nexxus",
  "trs",
  "aetna cvs",
].map((p) => p.toLowerCase().trim());

export function requiresHmoReferral(referral: Referral): boolean {
  const plan = referral.primaryCoverage?.plan?.trim();

  if (!plan) return false;

  return isHmoPlan(plan) || PLANS_REQUIRING_REFERRAL.some((pattern) => plan.toLowerCase().includes(pattern));
}

export type AutonomyUpcomingAppointmentCategory = "toxin_ha" | "mri" | "other";

// Tolerant patterns: eCW visit-type text has minor variation (suffixes,
// spacing, casing). Match the concept, not an exact string, so a rename
// like "Toxin HA F/U" or "MRI Brain" still classifies correctly. Observed
// vocab is logged in production by `logEncounterVocabulary` in the
// webecw provider-context step — grep that if a real visit-type ever fails
// to match here.
const BOTOX_APPOINTMENT_PATTERN = /\btoxin\s+ha\b/i;
const MRI_APPOINTMENT_PATTERN = /\bmri\b/i;

export function getRelevantUpcomingAppointmentType(
  encounters: EcwEncounter[] | null | undefined,
  exclusions: EcwEncounterExclusions,
  now: DateTime = DateTime.now()
): AutonomyUpcomingAppointmentCategory | null {
  if (!encounters?.length) return null;

  const today = now.toFormat("yyyy-MM-dd");

  const upcoming = encounters
    .filter((encounter) => {
      if (!encounter.date || encounter.date < today) return false;
      if (encounter.encType === EcwEncounterType.Telephone) return false;
      if (exclusions.statuses.includes(encounter.status)) return false;
      if (exclusions.visitTypes.includes(encounter.visitType)) return false;

      return true;
    })
    .sort((a, b) => {
      const dateA = a.date ?? "";
      const dateB = b.date ?? "";

      if (dateA !== dateB) return dateA < dateB ? -1 : 1;

      // Same-day tiebreak by encounterID — matches selectLatestEncounterDoctorId
      // and avoids sorting on `startTime`, whose format eCW does not document.
      return Number(a.encounterID) - Number(b.encounterID);
    });

  const nearest = upcoming[0];

  if (!nearest) return null;

  const visitType = nearest.visitType?.trim();

  if (!visitType) return "other";
  if (BOTOX_APPOINTMENT_PATTERN.test(visitType)) return "toxin_ha";
  if (MRI_APPOINTMENT_PATTERN.test(visitType)) return "mri";

  return "other";
}

const SLEEP_DIAGNOSIS_CODES = ["G47.30", "G47.33", "R06.83", "G47.419"];

function diagnosisListHasSleep(diagnosisList: Diagnosis[] | null | undefined): boolean {
  if (!diagnosisList?.length) return false;

  for (const d of diagnosisList) {
    if (d.diagnosisCode && SLEEP_DIAGNOSIS_CODES.some((pattern) => diagnosisCodeMatches(pattern, d.diagnosisCode!))) {
      return true;
    }
  }

  return false;
}

export function hasSleepDiagnosis(referral: Referral): boolean {
  return diagnosisListHasSleep(referral.diagnosisList);
}

export function sectionHasSleepDiagnosis(section: Section): boolean {
  const reviewed = getReviewedFieldsForKey(section.enrichedContent, BlockKey.REFERRAL_INFORMATION);

  return diagnosisListHasSleep(reviewed?.diagnosisList);
}

const UTSW_FAX_NUMBERS: ReadonlySet<string> = new Set([
  "+12146483161",
  "+18178822501",
  "+19722310360",
  "+12146458894",
  "+14696474871",
  "+12146452354",
  "+12146925141",
  "+14696474210",
  "+15127829316",
  "+12146458451",
  "+18178822441",
]);

function isUtswNumber(num: string | null): boolean {
  if (!num) return false;

  try {
    return UTSW_FAX_NUMBERS.has(formatPhoneNumber(num));
  } catch {
    return false;
  }
}

function isUtswReferral(referral: Referral, faxSenderNumber: string | null): boolean {
  const name = referral.referringLocation?.name?.toLowerCase() ?? "";

  if (name.includes("utsw") || name.includes("ut southwestern")) return true;

  return isUtswNumber(faxSenderNumber) || isUtswNumber(referral.referringLocation?.faxPhoneNumber ?? null);
}

const OPTUM_SERVE_FAX_NUMBERS: ReadonlySet<string> = new Set(["+16087837532"]);

function isOptumServeNumber(num: string | null): boolean {
  if (!num) return false;

  try {
    return OPTUM_SERVE_FAX_NUMBERS.has(formatPhoneNumber(num));
  } catch {
    return false;
  }
}

export function isOptumServeReferral(faxSenderNumber: string | null, referral?: Referral | null): boolean {
  return isOptumServeNumber(faxSenderNumber) || isOptumServeNumber(referral?.referringLocation?.faxPhoneNumber ?? null);
}

function getReferralLabel(referral: Referral, faxSenderNumber: string | null): string | null {
  if (isUtswReferral(referral, faxSenderNumber)) return " UTSW";
  if (isOptumServeReferral(faxSenderNumber, referral)) return " OPTUM SERVE";

  return null;
}

export function generateTeMessage(referral: Referral, task: Task, faxSenderNumber: string | null): string {
  const lines: string[] = [];

  const patientAge = calculateAge(referral.patient?.dateOfBirth ?? null);

  if (patientAge !== null && patientAge < 18) {
    lines.push("** MINOR **");
  }

  const referringProviderName = formatProviderName(referral.referringProvider);

  const labelSuffix = getReferralLabel(referral, faxSenderNumber) ?? "";

  lines.push(`Referral by ${referringProviderName}${labelSuffix}`);

  const providerInitials = getProviderInitials(referral);

  if (providerInitials) {
    lines.push(providerInitials);
  }

  const diagnosisText = formatDiagnosisTextForBody(referral);

  lines.push(diagnosisText);

  const payer = referral.primaryCoverage?.payer || "No insurance on file";
  const plan = referral.primaryCoverage?.payer ? referral.primaryCoverage.plan : null;
  const insuranceName = plan ? `${payer} ${plan}` : payer;

  lines.push(insuranceName);

  lines.push("");

  const missingItems = getMissingInformationSummary(referral, task);

  if (missingItems.length > 0) {
    const itemsList = missingItems.join(", ");

    lines.push(`Requested ${itemsList} - please check if they've arrived before reaching out to the provider`);
  }

  return lines.join("\n");
}

function formatProviderName(provider: Practitioner | null | undefined): string {
  if (!provider) return "Unknown Provider";

  const firstName = provider.givenName || "";
  const lastName = provider.familyName || "";

  if (!firstName && !lastName) return "Unknown Provider";

  return `${firstName} ${lastName}`.trim();
}

const PROVIDER_INITIALS_PLACEHOLDER = "[provider initials]";

function getProviderInitials(referral: Referral): string {
  const provider = referral.receivingProvider;

  if (!provider) return PROVIDER_INITIALS_PLACEHOLDER;

  const firstInitial = provider.givenName?.charAt(0)?.toUpperCase() || "";
  const lastInitial = provider.familyName?.charAt(0)?.toUpperCase() || "";

  if (!firstInitial && !lastInitial) return PROVIDER_INITIALS_PLACEHOLDER;

  return `${firstInitial}${lastInitial}`;
}

function formatDiagnosisText(referral: Referral): string {
  if (referral.reasonForReferral) {
    return simplifyMedicalText(referral.reasonForReferral);
  }

  if (referral.diagnosisList && referral.diagnosisList.length > 0) {
    const diagnosisNames: string[] = [];

    for (const d of referral.diagnosisList) {
      const parts = [
        d.diagnosisCode ? formatICD10CodeForDisplay(d.diagnosisCode) : null,
        d.diagnosisText ? simplifyMedicalText(d.diagnosisText) : null,
      ].filter(Boolean);

      if (parts.length > 0) {
        diagnosisNames.push(parts.join(" "));
      }
    }

    return diagnosisNames.join(", ");
  }

  return "Unspecified";
}

function getComparisonTokenCounts(text: string): Map<string, number> {
  const tokenCounts = new Map<string, number>();
  const tokens = text.toLowerCase().match(/[\p{L}\p{N}]+/gu) ?? [];

  for (const token of tokens) {
    if (token === "and") continue;

    tokenCounts.set(token, (tokenCounts.get(token) ?? 0) + 1);
  }

  return tokenCounts;
}

function isReasonRedundantWithDiagnosisTexts(reason: string | null, diagnosisTexts: string[]): boolean {
  if (!reason) return false;

  const reasonSegments = reason
    .split(";")
    .map((segment) => segment.trim())
    .filter(Boolean);

  if (reasonSegments.length === 0) return false;

  const diagnosisTokenCounts = diagnosisTexts.map(getComparisonTokenCounts);

  return reasonSegments.every((segment) => {
    const segmentTokenCounts = getComparisonTokenCounts(segment);

    if (segmentTokenCounts.size === 0) return false;

    return diagnosisTokenCounts.some((diagnosisTokenCountsForText) =>
      Array.from(segmentTokenCounts).every(([token, count]) => (diagnosisTokenCountsForText.get(token) ?? 0) >= count)
    );
  });
}

function formatDiagnosisTextForBody(referral: Referral): string {
  const diagnoses: { display: string; comparisonText: string | null }[] = [];

  for (const diagnosis of referral.diagnosisList ?? []) {
    const comparisonText = diagnosis.diagnosisText ? normalizeMedicalText(diagnosis.diagnosisText) : null;
    const displayText = diagnosis.diagnosisText ? simplifyMedicalText(diagnosis.diagnosisText) : null;
    const code = diagnosis.diagnosisCode ? formatICD10CodeForDisplay(diagnosis.diagnosisCode) : null;
    const display = [code, displayText].filter(Boolean).join(" ");

    if (display) {
      diagnoses.push({ display, comparisonText });
    }
  }

  const reason = referral.reasonForReferral ? simplifyMedicalText(referral.reasonForReferral) : null;
  const reasonText = referral.reasonForReferral ? normalizeMedicalText(referral.reasonForReferral) : null;

  if (diagnoses.length > 0) {
    const diagnosisText = diagnoses.map((diagnosis) => diagnosis.display).join(", ");

    return reason &&
      !isReasonRedundantWithDiagnosisTexts(
        reasonText,
        diagnoses.map((diagnosis) => diagnosis.comparisonText ?? "")
      )
      ? `${diagnosisText} - ${reason}`
      : diagnosisText;
  }

  if (reason) {
    return reason;
  }

  return "Unspecified";
}

function simplifyMedicalText(text: string): string {
  return normalizeMedicalText(text) || "Unspecified condition";
}

function normalizeMedicalText(text: string): string {
  let simplified = text.replace(/\b[A-Z]\d{2}(\.\d+)?\b/g, "");

  simplified = simplified.replace(/\([^)]+\)/g, "");

  simplified = simplified.replace(/\s+/g, " ").trim();

  // TODO(Autonomy): Add more simplification rules for acronyms and medical terminology

  return simplified;
}

export function getMissingInformationSummary(referral: Referral, task: Task): string[] {
  const missingItems: string[] = [...getAutonomyMissingReferralItems(task)];

  if (!referral.patient) {
    missingItems.push("Patient Information");
  } else {
    const patient = referral.patient;

    if (!patient.givenName || !patient.familyName) {
      missingItems.push("Patient Name");
    }

    if (!patient.dateOfBirth) {
      missingItems.push("Patient Date of Birth");
    }

    const hasPhone = patient.mobilePhoneNumber || patient.homePhoneNumber;

    if (!hasPhone) {
      missingItems.push("Patient Phone Number");
    }

    if (!patient.homeAddress) {
      missingItems.push("Patient Address");
    }
  }

  if (referral.primaryCoverage) {
    if (!referral.primaryCoverage.payer) {
      missingItems.push("Insurance Plan Name");
    }

    if (!referral.primaryCoverage.subscriberId) {
      missingItems.push("Insurance Member ID");
    }
  }

  return missingItems;
}

export enum TeReasonInsuranceStatus {
  ACTIVE = "active",
  PENDING = "pending",
  MISSING = "missing",
}

export enum AutonomyPatientEncounterStatus {
  NEW_PATIENT = "new_patient",
  ESTABLISHED_PATIENT = "established_patient",
}

export type TeReasonContext = {
  patientEncounterStatus: AutonomyPatientEncounterStatus | null;
  providerInitials: string;
  diagnosis: string;
  hasMissingInfo: boolean;
  requiresHmoReferral: boolean;
  isUrgent: boolean;
  insuranceStatus: TeReasonInsuranceStatus;
};

export function buildTeReasonContext(
  referral: Referral,
  task: Task,
  patientEncounterStatus: AutonomyPatientEncounterStatus | null
): TeReasonContext {
  const providerInitials = getProviderInitials(referral);
  const diagnosis = formatDiagnosisText(referral);
  const missingItems = getMissingInformationSummary(referral, task);
  const needsHmoReferral = requiresHmoReferral(referral);
  const isUrgent = isStatOrUrgentPriority(referral.schedulingPriority);

  let insuranceStatus: TeReasonInsuranceStatus = TeReasonInsuranceStatus.ACTIVE;

  if (!referral.primaryCoverage) {
    insuranceStatus = TeReasonInsuranceStatus.MISSING;
  } else if (!referral.primaryCoverage.subscriberId) {
    insuranceStatus = TeReasonInsuranceStatus.PENDING;
  }

  return {
    patientEncounterStatus,
    providerInitials,
    diagnosis,
    hasMissingInfo: missingItems.length > 0,
    requiresHmoReferral: needsHmoReferral,
    isUrgent,
    insuranceStatus,
  };
}

export function generateTeReasonText(context: TeReasonContext): string {
  const {
    patientEncounterStatus,
    providerInitials,
    diagnosis,
    hasMissingInfo,
    requiresHmoReferral: needsHmoReferral,
    insuranceStatus,
  } = context;

  const coreReason = formatTeReasonCore(patientEncounterStatus, providerInitials, diagnosis);

  const prefixes: string[] = [];

  if (hasMissingInfo) prefixes.push("Missing Info");

  let result = prefixes.length > 0 ? `${prefixes.join(" - ")} - ${coreReason}` : coreReason;

  if (insuranceStatus === TeReasonInsuranceStatus.PENDING || insuranceStatus === TeReasonInsuranceStatus.MISSING) {
    result = `${result} - Insurance Verification Needed`;
  }

  return result;
}

function formatTeReasonCore(
  patientEncounterStatus: AutonomyPatientEncounterStatus | null,
  providerInitials: string,
  diagnosis: string
): string {
  const patientTypeInitials = getPatientTypeInitials(patientEncounterStatus);
  const parts: string[] = [];

  if (patientTypeInitials) {
    parts.push(patientTypeInitials);
  }

  if (providerInitials) {
    parts.push(providerInitials);
  }

  parts.push("Referral");

  return `${parts.join(" ")} - ${diagnosis}`;
}

function getPatientTypeInitials(status: AutonomyPatientEncounterStatus | null): string | null {
  if (status === AutonomyPatientEncounterStatus.NEW_PATIENT) {
    return "NP";
  }

  if (status === AutonomyPatientEncounterStatus.ESTABLISHED_PATIENT) {
    return "EP";
  }

  return null;
}

export function getAutonomyIntakeClassification(classification: Classification): Classification {
  return AUTONOMY_REFERRAL_RELATED_CLASSIFICATIONS.has(classification) ? Classification.REFERRAL : classification;
}

export function getAutonomySelfExtractableClassifications(): Classification[] {
  return [Classification.IDENTIFICATION_CARD, Classification.INSURANCE_CARD, Classification.INTAKE];
}

export function getAutonomyDispositionEmailRecipients(): string[] {
  return ["medicalrecords@texasneurology.com"];
}

export const AUTONOMY_ENCOUNTER_FILTER_OPTIONS: FilterEncounterOptions = {
  ...DEFAULT_ENCOUNTER_FILTER_OPTIONS,
  excludedStatuses: [
    ...(DEFAULT_ENCOUNTER_FILTER_OPTIONS.excludedStatuses ?? []),
    EncounterStatus.PLANNED,
    EncounterStatus.UNKNOWN,
  ],
  excludeNoType: true,
};

export const AUTONOMY_WEBECW_ENCOUNTER_EXCLUSIONS: EcwEncounterExclusions = {
  statuses: [],
  visitTypes: ["Migrated"],
};

const FOCUS_NEURO_HEALTH_EMAIL = "Info@focusneurohealth.com";

const NOT_RELEVANT_PROVIDER_FORWARDING: Record<string, { email: string }> = {
  "1528293628": { email: FOCUS_NEURO_HEALTH_EMAIL },
  "1902528391": { email: FOCUS_NEURO_HEALTH_EMAIL },
};

function isNotRelevantProviderNpi(npi: string | null): boolean {
  if (!npi) return false;

  return npi in NOT_RELEVANT_PROVIDER_FORWARDING;
}

function isNotRelevantProvider(referral: Referral): boolean {
  return isNotRelevantProviderNpi(referral.receivingProvider?.npiNumber ?? null);
}

export function getAutonomyReferralRoutingStrategy(referral?: Referral): ReferralRoutingStrategy[] {
  if (referral && isNotRelevantProvider(referral)) {
    return [ReferralRoutingStrategy.EMAIL_ONLY_NO_PATIENT_CREATION];
  }

  return [];
}

export function getAutonomyReferralEmailRecipients(referral: Referral): string[] {
  const npi = referral.receivingProvider?.npiNumber;

  if (!npi) return [];

  const forwarding = NOT_RELEVANT_PROVIDER_FORWARDING[npi];

  if (!forwarding) return [];

  return [forwarding.email];
}

export function getAutonomyDocRoutingStrategy(section: Section, patient: Patient): DocRoutingStrategy[] {
  const npi = getReceivingProviderNpi(section);

  if (isNotRelevantProviderNpi(npi)) return [DocRoutingStrategy.EMAIL];

  if (isPatientNotInEhr(patient)) return [DocRoutingStrategy.EMAIL];

  return [DocRoutingStrategy.EHR];
}

const AUTONOMY_AUTO_CLOSE_SAMPLING_RATES = new Map<Classification, number>([
  [Classification.PRIOR_AUTHORIZATION, 0.95],
  [Classification.PHARMACY_RELATED, 0.95],
  [Classification.MEDICAL_RECORD_REQUEST, 0.95],
  [Classification.REFERRAL_STATUS_REQUEST, 0.5],
  [Classification.PROGRESS_NOTE, 0.95],
  [Classification.LAB_RESULTS, 0.9],
  [Classification.LAB_RESULTS_ORDER_FORM_NEEDS_SIGNATURE, 0.9],
  [Classification.CARE_PLAN, 0.5],
  [Classification.IMAGING_RESULTS_OTHER, 0.5],
  [Classification.DME_ORDER, 0.5],
  [Classification.MEDICAL_HISTORY, 0.5],
]);

export function shouldAutonomyAutoCloseTask(classification: Classification): boolean {
  return AUTONOMY_AUTO_CLOSE_SAMPLING_RATES.has(classification);
}

export function getAutonomyAutoCloseSamplingRate(section: Section): number {
  return AUTONOMY_AUTO_CLOSE_SAMPLING_RATES.get(section.classification) ?? 0;
}

export function getAutonomyDocRoutingEmailRecipients(section: Section): string[] {
  const npi = getReceivingProviderNpi(section);
  const forwarding = npi ? NOT_RELEVANT_PROVIDER_FORWARDING[npi] : null;

  if (forwarding) return [forwarding.email];

  return getAutonomyDispositionEmailRecipients();
}

export const AUTONOMY_AUTO_INTAKE_CONFIG: AutoIntakeConfig = {
  byClassification: {
    [Classification.IMAGING_RESULTS_MRI]: { rate: 0.9 },
    [Classification.LAB_RESULTS_ORDER_FORM_NEEDS_SIGNATURE]: { rate: 0.9 },
    [Classification.MEDICAL_HISTORY]: { rate: 0.9 },
    [Classification.PHARMACY_RELATED]: { rate: 0.95 },
    [Classification.PRIOR_AUTHORIZATION]: { rate: 0.95 },
    [Classification.RESPONSE_TO_VH_REQUEST]: { rate: 0.0 },
  },
  blockChecks: [AutoIntakeBlockCheck.MATCHED_PROVIDER_NPI, AutoIntakeBlockCheck.GEOCODED_LOCATION],
};
